import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaDownload, FaSms } from "react-icons/fa";
import { apihubGet, apihubPost, apihubUpload } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { allowDevMockFallback } from "../../utils/devMockFallback";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { getUserName } from "../../utils/auth";
import { normalizePhone, parseCsv } from "../../utils/parseCsv";
import { isApprovedTemplate } from "../../utils/templatePreview";
import CampaignTemplatePicker from "./CampaignTemplatePicker";

const STEPS = [
  "Campaign Name",
  "Upload CSV",
  "SMS Settings",
  "WhatsApp & Launch",
];

const MAP_FIELDS = [
  { value: "", label: "Select an attribute" },
  { value: "username", label: "UserName" },
  { value: "phonenumber", label: "PhoneNumber" },
  { value: "tags", label: "Tags" },
  { value: "email", label: "Email" },
];

const SMS_STATUS_OPTIONS = ["sent", "delivered", "read", "failed"];

const COUNTRIES = [
  { code: "select", name: "country", iso: "name" },
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1", name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
];

const MOCK_WA_TEMPLATES = [
  {
    _id: "69f6ffb2b1ae76dbff45c051",
    template_id: "4513437452222207",
    name: "democonfirmation_classopt",
    category: "MARKETING",
    template_status: "APPROVED",
  },
];

const MOCK_SMS_TEMPLATES = [
  {
    _id: "sms-mock-1",
    smsTemplateName: "Class reminder SMS",
    smsTemplateMessage:
      "Hi {#var#}, your class is scheduled. Reply STOP to opt out.",
  },
];

const SAMPLE_CSV = `username,mobile,tags
Student One,9876543210,Class11
Student Two,9876543211,Class10`;

const CampaignV2 = () => {
  const { projectId, phoneNoId, wabaId } = useProjectPhone();

  const [step, setStep] = useState(0);
  const [campaignName, setCampaignName] = useState("");

  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [headerMappings, setHeaderMappings] = useState({});
  const [csvFileUrl, setCsvFileUrl] = useState("");
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [country, setCountry] = useState(COUNTRIES[1]);
  const [customAttributes, setCustomAttributes] = useState([]);

  const [triggerAll, setTriggerAll] = useState(false);
  const [triggerFailed, setTriggerFailed] = useState(false);
  const [triggerStatusChoice, setTriggerStatusChoice] = useState(false);
  const [statusRadio, setStatusRadio] = useState("");

  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState("");
  const [smsPreview, setSmsPreview] = useState("");
  const [smsVarPlaceholders, setSmsVarPlaceholders] = useState([]);
  const [smsPlaceholderValues, setSmsPlaceholderValues] = useState({});

  const [waTemplates, setWaTemplates] = useState([]);
  const [selectedWaTemplate, setSelectedWaTemplate] = useState(null);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);

  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState("");
  const [error, setError] = useState("");

  const mapFieldOptions = useMemo(() => {
    const extra = customAttributes
      .filter((a) => a && !MAP_FIELDS.some((f) => f.value === a.toLowerCase()))
      .map((a) => ({ value: a.toLowerCase(), label: a }));
    return [...MAP_FIELDS, ...extra];
  }, [customAttributes]);

  const smsTriggerEvents = useMemo(() => {
    if (triggerAll) return ["all"];
    const events = [];
    if (triggerFailed) events.push("failed");
    if (statusRadio) events.push(statusRadio);
    return events;
  }, [triggerAll, triggerFailed, statusRadio]);

  const phoneHeader = useMemo(
    () =>
      Object.entries(headerMappings).find(
        ([, field]) => field === "phonenumber",
      )?.[0],
    [headerMappings],
  );

  const csvheadersmap = useMemo(
    () =>
      csvHeaders.map((header) => ({
        header,
        field: headerMappings[header] || "",
        default: "",
      })),
    [csvHeaders, headerMappings],
  );

  const excelData = useMemo(() => {
    if (!phoneHeader) return [];
    const dialCode = country.code?.replace("+", "") || "91";
    return csvRows
      .map((row) => {
        const phoneNumber = normalizePhone(row[phoneHeader], dialCode);
        if (!phoneNumber) return null;
        const entry = { phoneNumber, ...row };
        const nameKey = Object.entries(headerMappings).find(
          ([, field]) => field === "username",
        )?.[0];
        if (nameKey) entry.username = row[nameKey];
        return entry;
      })
      .filter(Boolean);
  }, [csvRows, headerMappings, phoneHeader, country.code]);

  const selectedSmsTemplate = useMemo(
    () => smsTemplates.find((t) => t._id === selectedSmsTemplateId),
    [smsTemplates, selectedSmsTemplateId],
  );

  const fetchAttributes = useCallback(async () => {
    if (!phoneNoId) return;
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/getattributesdetail/${phoneNoId}`,
      );
      const attrs = res?.data?.attributes || res?.attributes || [];
      setCustomAttributes(
        Array.isArray(attrs) ? attrs.map((a) => a.name || a).filter(Boolean) : [],
      );
    } catch {
      setCustomAttributes([]);
    }
  }, [phoneNoId]);

  const fetchProviders = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/fetchprovidername/${projectId}`,
      );
      const list = res?.data || [];
      setProviders(Array.isArray(list) ? list : []);
    } catch {
      setProviders([]);
    }
  }, [projectId]);

  const getProviderKey = (provider) =>
    provider?._id || provider?.providerName || provider?.providername || "";

  const getProviderLabel = (provider) =>
    provider?.providerName || provider?.providername || provider?.name || "—";

  const fetchSmsTemplatesByProvider = useCallback(
    async (pid) => {
      if (!pid || !projectId) {
        setSmsTemplates([]);
        return;
      }
      try {
        const res = await apihubPost(
          "/getInteractedCustomerDetail/fetchsmstemplatebyprovider",
          {
            sms_config_id: pid,
            providerid: pid,
            projectid: projectId,
          },
        );
        const list = res?.data || [];
        setSmsTemplates(
          Array.isArray(list) && list.length
            ? list
            : allowDevMockFallback()
              ? MOCK_SMS_TEMPLATES
              : [],
        );
      } catch {
        setSmsTemplates(allowDevMockFallback() ? MOCK_SMS_TEMPLATES : []);
      }
    },
    [projectId],
  );

  const fetchWaTemplates = useCallback(async () => {
    if (!phoneNoId) return;
    setLoadingWaTemplates(true);
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneNoId}?page=1&limit=100&sortBy=createdAt&sortOrder=desc`,
      );
      const list = res?.data || res?.templates || [];
      const next =
        Array.isArray(list) && list.length
          ? list
          : allowDevMockFallback()
            ? MOCK_WA_TEMPLATES
            : [];
      setWaTemplates(next);
      setSelectedWaTemplate((prev) =>
        prev && isApprovedTemplate(prev) ? prev : null,
      );
    } catch {
      setWaTemplates(allowDevMockFallback() ? MOCK_WA_TEMPLATES : []);
      setSelectedWaTemplate(null);
    } finally {
      setLoadingWaTemplates(false);
    }
  }, [phoneNoId]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  useEffect(() => {
    if (step === 2) fetchProviders();
  }, [step, fetchProviders]);

  useEffect(() => {
    if (step === 3) fetchWaTemplates();
  }, [step, fetchWaTemplates]);

  const parseSmsVars = (message) => {
    const re = /{#(.*?)#}|{##}/g;
    const vars = [];
    let match;
    while ((match = re.exec(message || "")) !== null) {
      vars.push(match[1] || "");
    }
    return vars;
  };

  const applyCsvLocally = (text, file) => {
    const { headers, rows } = parseCsv(text || "");
    setCsvHeaders(headers);
    setCsvRows(rows);
    const initial = {};
    headers.forEach((h) => {
      const lower = h.toLowerCase();
      let field = "";
      if (lower.includes("name") && !lower.includes("phone")) field = "username";
      else if (lower.includes("mobile") || lower.includes("phone")) {
        field = "phonenumber";
      } else if (lower.includes("tag")) field = "tags";
      initial[h] = field;
    });
    setHeaderMappings(initial);
    setCsvFile(file);
  };

  const uploadCsv = async (file) => {
    if (!file || !projectId) return;
    setUploadingCsv(true);
    setError("");
    try {
      const form = new FormData();
      form.append("csvFile", file);
      const res = await apihubUpload(
        `/getInteractedCustomerDetail/upload/${projectId}`,
        form,
      );
      if (res?.publicUrl) setCsvFileUrl(res.publicUrl);
      const headers = (res?.headers || []).map((h) =>
        typeof h === "string" ? h.toLowerCase() : h,
      );
      if (headers.length) {
        setCsvHeaders(headers);
        const initial = headers.reduce((acc, h) => ({ ...acc, [h]: "" }), {});
        setHeaderMappings(initial);
      }
      if (Array.isArray(res?.data) && res.data.length) {
        setCsvRows(
          res.data.map((row) =>
            row && typeof row === "object"
              ? Object.fromEntries(
                  Object.entries(row).map(([k, v]) => [k.toLowerCase(), v]),
                )
              : row,
          ),
        );
      } else {
        const reader = new FileReader();
        reader.onload = (e) => applyCsvLocally(e.target.result, file);
        reader.readAsText(file);
        return;
      }
      setCsvFile(file);
    } catch {
      const reader = new FileReader();
      reader.onload = (e) => applyCsvLocally(e.target.result, file);
      reader.readAsText(file);
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    uploadCsv(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_wa_sms_campaign.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onSmsTemplateChange = (templateId) => {
    setSelectedSmsTemplateId(templateId);
    const tpl = smsTemplates.find((t) => t._id === templateId);
    const msg =
      tpl?.smsTemplateMessage ||
      tpl?.smstemplatemessage ||
      tpl?.templateMessage ||
      "";
    setSmsPreview(msg);
    const vars = parseSmsVars(msg);
    setSmsVarPlaceholders(vars);
    setSmsPlaceholderValues({});
  };

  const onProviderChange = (pid) => {
    setProviderId(pid);
    setSelectedSmsTemplateId("");
    setSmsPreview("");
    setSmsVarPlaceholders([]);
    fetchSmsTemplatesByProvider(pid);
  };

  const canNext = () => {
    if (step === 0) return campaignName.trim().length > 0;
    if (step === 1) {
      const mapped = Object.values(headerMappings);
      return (
        csvRows.length > 0 &&
        mapped.includes("username") &&
        mapped.includes("phonenumber") &&
        country.code !== "select"
      );
    }
    if (step === 2) {
      return (
        smsTriggerEvents.length > 0 &&
        Boolean(providerId) &&
        Boolean(selectedSmsTemplateId)
      );
    }
    if (step === 3) return Boolean(selectedWaTemplate);
    return true;
  };

  const handleLaunch = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        campaignName: campaignName.trim(),
        campaigntype: "WHATSAPP_SMS_CSVBROADCAST",
        csvheadersmap,
        headerValues: {},
        bodyValues: {},
        buttonValues: {},
        selectedTemplate: selectedWaTemplate,
        phoneNumbers: excelData.map((r) => r.phoneNumber),
        headerMappings,
        defaultCountryCode: country,
        templatemediaurl: { field: "", default: "" },
        submittedaudience: excelData.length,
        csvfileurl: csvFileUrl,
        csvfilename: csvFile?.name || "upload.csv",
        currentPhoneNumberId: phoneNoId,
        currentWabaId: wabaId || phoneNoId,
        carouselcardmediaurl: {},
        carouselcardplaceholdervalues: {},
        templatetypelatitude: { field: "", default: "" },
        templatetypelongtitude: { field: "", default: "" },
        templatetypelocationname: { field: "", default: "" },
        templatetypelocationaddress: { field: "", default: "" },
        senderUserName: getUserName() || "Admin",
        isScheduled: scheduleOn ? "On" : "Off",
        selectedDate: scheduleOn ? scheduleDate : "",
        selectedTime: scheduleOn ? scheduleTime : "",
        projectId,
        smstriggerevent: smsTriggerEvents,
        selectedsmstemplate: selectedSmsTemplateId,
        smstemplateplaceholdervalues: smsPlaceholderValues,
        exceldata: excelData,
      };

      const res = await apihubPost(
        "/getInteractedCustomerDetail/send-broadcast02",
        payload,
      );
      setSuccessId(res?.campaignid || res?.data?.campaignid || "ok");
    } catch (err) {
      setError(
        err.message ||
          "Launch failed. Sign in to apihub with a valid token to send WA+SMS campaigns.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="biz-panel-page biz-broadcast-page">
      <div className="biz-panel-header">
        <h1>
          <FaSms />
          WA+SMS Broadcast
        </h1>
      </div>

      <p className="biz-v2-tagline">
        Boost campaign reach by delivering messages through both WhatsApp and SMS.
      </p>

      <div className="biz-broadcast-steps">
        {STEPS.map((label, idx) => (
          <div
            key={label}
            className={`biz-broadcast-step ${
              idx === step ? "active" : idx < step ? "done" : ""
            }`}
          >
            <span className="biz-broadcast-step-num">
              {idx < step ? <FaCheck /> : idx + 1}
            </span>
            <span className="biz-broadcast-step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="biz-broadcast-body">
        {successId ? (
          <div className="biz-broadcast-success">
            <h2>Campaign launched</h2>
            <p>
              WA+SMS campaign &quot;{campaignName}&quot; submitted for{" "}
              {excelData.length} recipient(s).
            </p>
            <Link
              to={businessPath(projectId, "fetchingallcampaign")}
              className="biz-btn-primary"
            >
              View Campaign Report
            </Link>
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="biz-broadcast-hero">
                <h2>Campaign Name</h2>
                <p>Pick something that describes your audience &amp; goals.</p>
                <input
                  type="text"
                  placeholder="Enter Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </div>
            )}

            {step === 1 && (
              <div className="biz-csv-upload">
                <div className="biz-csv-upload-head">
                  <h2>Upload CSV File</h2>
                  <button
                    type="button"
                    className="biz-btn-outline biz-csv-sample-btn"
                    onClick={downloadSample}
                  >
                    <FaDownload />
                    Download Sample CSV file
                  </button>
                </div>
                <div
                  className="biz-csv-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                >
                  <p>Drag and drop a CSV file here or click to upload</p>
                  <label className="biz-btn-primary biz-csv-file-label">
                    {uploadingCsv ? "Uploading…" : "Choose File"}
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="biz-csv-file-input"
                      disabled={uploadingCsv}
                      onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                  </label>
                  {csvFile && (
                    <p className="biz-csv-file-name">
                      {csvFile.name} · {csvRows.length} rows
                    </p>
                  )}
                </div>

                {csvHeaders.length > 0 && (
                  <div className="biz-csv-mapping">
                    <h2>Map CSV Headers to Attributes</h2>
                    <label className="biz-csv-country">
                      Default Country Code
                      <select
                        value={country.iso}
                        onChange={(e) => {
                          const next = COUNTRIES.find(
                            (c) => c.iso === e.target.value,
                          );
                          if (next) setCountry(next);
                        }}
                      >
                        {COUNTRIES.filter((c) => c.code !== "select").map(
                          (c) => (
                            <option key={c.iso} value={c.iso}>
                              {c.name} ({c.code})
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                    <div className="biz-csv-map-list">
                      {csvHeaders.map((header) => (
                        <div key={header} className="biz-csv-map-row">
                          <span className="biz-csv-map-header">{header}</span>
                          <select
                            value={headerMappings[header] || ""}
                            onChange={(e) =>
                              setHeaderMappings((prev) => ({
                                ...prev,
                                [header]: e.target.value,
                              }))
                            }
                          >
                            {mapFieldOptions.map((opt) => (
                              <option key={opt.value || "empty"} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <p className="biz-broadcast-count">
                      {excelData.length} valid phone numbers · map UserName and
                      PhoneNumber to continue
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="biz-v2-sms-panel">
                <h2>SMS fallback settings</h2>
                <p className="biz-panel-hint">
                  Select when SMS should be triggered. You can select multiple
                  options like failed and sent/delivered/read.
                </p>

                <div className="biz-v2-trigger-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={triggerAll}
                      onChange={(e) => {
                        setTriggerAll(e.target.checked);
                        if (e.target.checked) {
                          setTriggerFailed(false);
                          setTriggerStatusChoice(false);
                          setStatusRadio("");
                        }
                      }}
                    />
                    All
                  </label>
                  <label className={triggerAll ? "biz-v2-disabled" : ""}>
                    <input
                      type="checkbox"
                      checked={triggerFailed}
                      disabled={triggerAll}
                      onChange={(e) => setTriggerFailed(e.target.checked)}
                    />
                    Failed
                  </label>
                  <label className={triggerAll ? "biz-v2-disabled" : ""}>
                    <input
                      type="checkbox"
                      checked={triggerStatusChoice}
                      disabled={triggerAll}
                      onChange={(e) => {
                        setTriggerStatusChoice(e.target.checked);
                        if (!e.target.checked) setStatusRadio("");
                      }}
                    />
                    Choose any one (sent/delivered/failed)
                  </label>
                </div>

                {triggerStatusChoice && !triggerAll && (
                  <div className="biz-v2-status-radios">
                    {SMS_STATUS_OPTIONS.map((status) => (
                      <label key={status}>
                        <input
                          type="radio"
                          name="sms-status"
                          value={status}
                          checked={statusRadio === status}
                          onChange={() => setStatusRadio(status)}
                        />
                        {status}
                      </label>
                    ))}
                  </div>
                )}

                <label className="biz-v2-field">
                  Provider
                  <select
                    value={providerId}
                    onChange={(e) => onProviderChange(e.target.value)}
                    required
                  >
                    <option value="">Select provider</option>
                    {providers.map((p) => {
                      const key = getProviderKey(p);
                      return (
                        <option key={key} value={key}>
                          {getProviderLabel(p)}
                        </option>
                      );
                    })}
                  </select>
                </label>

                {providerId && (
                  <div className="biz-v2-sms-template-row">
                    <label className="biz-v2-field">
                      Select the SMS template
                      <select
                        value={selectedSmsTemplateId}
                        onChange={(e) => onSmsTemplateChange(e.target.value)}
                      >
                        <option value="">Choose a template</option>
                        {smsTemplates.map((tpl) => (
                          <option key={tpl._id} value={tpl._id}>
                            {tpl.smsTemplateName ||
                              tpl.smstemplatename ||
                              tpl.templateName ||
                              "—"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="biz-v2-sms-preview">
                      <h3>SMS Template Preview</h3>
                      <p>{smsPreview || "No template selected"}</p>
                    </div>
                  </div>
                )}

                {providerId && smsTemplates.length === 0 && (
                  <p className="biz-panel-hint">
                    No templates found for this provider.
                  </p>
                )}

                {smsVarPlaceholders.length > 0 && (
                  <div className="biz-v2-sms-vars">
                    <p className="biz-v2-vars-title">
                      Please fill sms template variables for (#var#)
                    </p>
                    {smsVarPlaceholders.map((_, idx) => (
                      <div key={idx} className="biz-csv-map-row">
                        <span>Variable {idx + 1}</span>
                        <select
                          value={smsPlaceholderValues[idx]?.field || ""}
                          onChange={(e) =>
                            setSmsPlaceholderValues((prev) => ({
                              ...prev,
                              [idx]: {
                                ...prev[idx],
                                field: e.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">Select field name</option>
                          {mapFieldOptions
                            .filter((o) => o.value)
                            .map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                        </select>
                        <input
                          type="text"
                          placeholder={`Default value for ${idx + 1}`}
                          value={smsPlaceholderValues[idx]?.default || ""}
                          onChange={(e) =>
                            setSmsPlaceholderValues((prev) => ({
                              ...prev,
                              [idx]: {
                                ...prev[idx],
                                default: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <>
                <CampaignTemplatePicker
                  templates={waTemplates}
                  selectedTemplate={selectedWaTemplate}
                  onSelect={setSelectedWaTemplate}
                  loading={loadingWaTemplates}
                />

                <div className="biz-broadcast-schedule biz-v2-schedule">
                  <h3>Schedule the campaign</h3>
                  <label className="biz-broadcast-schedule-toggle">
                    <input
                      type="checkbox"
                      checked={scheduleOn}
                      onChange={(e) => setScheduleOn(e.target.checked)}
                    />
                    Enable scheduling
                  </label>
                  {scheduleOn && (
                    <div className="biz-broadcast-schedule-fields">
                      <label>
                        Select Date:
                        <input
                          type="date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                        />
                      </label>
                      <label>
                        Select Time:
                        <input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </div>

                <div className="biz-broadcast-summary">
                  <h3>Summary</h3>
                  <ul>
                    <li>
                      <strong>Campaign:</strong> {campaignName}
                    </li>
                    <li>
                      <strong>Recipients:</strong> {excelData.length}
                    </li>
                    <li>
                      <strong>SMS triggers:</strong>{" "}
                      {smsTriggerEvents.join(", ") || "—"}
                    </li>
                    <li>
                      <strong>SMS template:</strong>{" "}
                      {selectedSmsTemplate?.smsTemplateName ||
                        selectedSmsTemplate?.smstemplatename ||
                        "—"}
                    </li>
                    <li>
                      <strong>WhatsApp template:</strong>{" "}
                      {selectedWaTemplate?.name || "—"}
                    </li>
                  </ul>
                </div>
              </>
            )}

            {error && <p className="biz-panel-error">{error}</p>}

            <div className="biz-broadcast-nav">
              {step > 0 && (
                <button
                  type="button"
                  className="biz-btn-outline"
                  onClick={() => setStep((s) => s - 1)}
                  disabled={submitting}
                >
                  Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  className="biz-btn-primary"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext() || uploadingCsv}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="biz-btn-primary"
                  onClick={handleLaunch}
                  disabled={submitting || !canNext()}
                >
                  {submitting ? "Launching…" : "Launch Campaign"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignV2;
